<template>
  <div>
    <!-- Error Alert -->
    <b-alert
      v-if="errors"
      variant="danger"
      show
      dismissible
      class="mb-3"
      @dismissed="errors = ''"
    >
      {{ errors }}
    </b-alert>

    <b-row>
      <b-col
        md="3"
      >
        <b-form-group label="Item Name">
          <custom-v-select
            v-model="selectedProduct"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="filterProductOptions"
            :loading="productLoading"
            label="name"
            placeholder="Search Item Name"
            :deselect-from-dropdown="true"
            :filterable="false"
            @search="searchProducts"
            @input="handleProductChange"
            @open="onDropdownOpen"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="3"
      >
        <b-form-group label="Batch">
          <custom-v-select
            v-model="selectedBatches"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="batchOptions"
            label="text"
            placeholder="Search Batch Name"
            :loading="batchLoading"
            multiple
            :close-on-select="false"
            :deselect-from-dropdown="true"
            :filterable="false"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group
          label="Status"
        >
          <custom-v-select
            v-model="selectedStatusOption"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            label="text"
            placeholder="Select Option"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="2">
        <b-form-group
          label="Bonus Type"
        >
          <custom-v-select
            v-model="selectedBonusTypeOption"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="bonusTypeOptions"
            label="text"
            placeholder="Select Option"
            multiple
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group
          label="Start Date"
        >
          <flat-picker
            v-model="startDateRange"
            placeholder="Select Date Range"
            :config="{ mode: 'range'}"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group
          label="End Date"
        >
          <flat-picker
            v-model="endDateRange"
            placeholder="Select Date Range"
            :config="{ mode: 'range'}"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  VBTooltip, BRow, BCol, BFormGroup, BAlert,
} from 'bootstrap-vue'
import axios from 'axios'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import dateMixin from '@/mixins/dateMixin'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BAlert,
    CustomVSelect,
    FlatPicker,
  },
  mixins: [dateMixin],
  data() {
    return {
      errors: '',
      selectedProduct: '',
      productOptions: [],
      filterProductOptions: [], // Filtered list for display
      allProducts: [], // Store all products for client-side filtering
      productLoading: false,
      selectedBatches: [],
      batchOptions: [],
      batchLoading: false,
      debounceTimer: null,
      searchTimeout: null,
      selectedStatusOption: [],
      selectedBonusTypeOption: [],
      statusOptions: [
        { text: 'Active', value: true },
        { text: 'InActive', value: 'false' },
      ],
      bonusTypeOptions: [
        { text: 'Gift', value: 'gift' },
        { text: 'Lot', value: 'lot' },
        { text: 'Net Rate', value: 'rate' },
        { text: 'Free Product', value: 'free_product' },
      ],
      startDateRange: null,
      endDateRange: null,
    }
  },
  watch: {
    startDateRange(newValue) {
      if (newValue) {
        const [fromDate, toDate] = newValue.split(' to ')
        if (fromDate && toDate) {
          this.filtersChangeHandler()
        }
      } else if (newValue === '') {
        this.filtersChangeHandler()
      }
    },
    endDateRange(newValue) {
      if (newValue) {
        const [fromDate, toDate] = newValue.split(' to ')
        if (fromDate && toDate) {
          this.filtersChangeHandler()
        }
      } else if (newValue === '') {
        this.filtersChangeHandler()
      }
    },
  },
  async created() {
    // Fetch all products on component load
    await this.fetchAllProducts()
  },
  beforeDestroy() {
    // Clean up timer when component is destroyed
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    debouncedFiltersChange() {
      // Clear the existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      // Set a new timer
      this.debounceTimer = setTimeout(() => {
        this.filtersChangeHandler()
      }, 500) // 500ms delay
    },

    // Fetch all products once on page load
    async fetchAllProducts() {
      this.productLoading = true
      try {
        const productRes = await axios.get('/marg/product_options')
        this.allProducts = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: product.name,
          rate: parseFloat(product.rate),
          mrp: parseFloat(product.mrp),
        }))
        this.productOptions = this.allProducts
        // Initially show first 15 products for better performance
        this.filterProductOptions = this.allProducts.slice(0, 15)
        this.productLoading = false
      } catch (error) {
        this.errors = error.response?.data?.error || 'Failed to fetch products. Please try again.'
        this.productOptions = []
        this.allProducts = []
        this.filterProductOptions = []
        this.productLoading = false
      }
    },

    // Handle dropdown open event - show initial products
    onDropdownOpen() {
      if (!this.filterProductOptions.length && this.allProducts.length) {
        this.filterProductOptions = this.allProducts.slice(0, 15)
      }
    },

    // Client-side search function with minimal debounce
    searchProducts(query) {
      // Clear the existing timer
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Set a new timer for debouncing (shorter delay for better UX)
      this.searchTimeout = setTimeout(() => {
        if (!query || query.trim() === '') {
          // If no query, show first 15 products
          this.filterProductOptions = this.allProducts.slice(0, 15)
        } else {
          // Filter products by name (case-insensitive)
          const searchString = query.toLowerCase()
          const filtered = this.allProducts.filter(product => product.name.toLowerCase().includes(searchString))
          // Limit to first 50 results for performance
          this.filterProductOptions = filtered.slice(0, 50)
        }
      }, 200) // Reduced to 200ms for faster response
    },

    async fetchBatchOptions(productCode) {
      const params = { code: productCode }

      this.batchLoading = true
      this.batchLoadingError = null

      try {
        const res = await axios.get(`/marg/get_erp_batches?${getParsedQueryString(params)}`)
        const batchOptions = res.data.batches.map(batch => ({
          id: batch.id,
          text: batch.batch,
        }))
        this.batchOptions = batchOptions
        this.batchLoading = false
      } catch (error) {
        this.errors = error?.response?.data?.error || 'Something went wrong'
        this.batchLoading = false
      }
    },
    handleProductChange(selectedProduct) {
      this.selectedProduct = selectedProduct || []
      this.selectedBatches = []
      this.batchOptions = []
      if (selectedProduct && selectedProduct.code) {
        this.fetchBatchOptions(selectedProduct.code)
      }
      this.filtersChangeHandler()
    },
    filtersChangeHandler() {
      const filtersdata = {}
      if (this.selectedStatusOption) {
        filtersdata.is_active = this.selectedStatusOption.value
      }
      if (this.selectedBonusTypeOption) {
        filtersdata.bonus_type = this.selectedBonusTypeOption.map(item => item.value)
      }
      if (this.selectedProduct) {
        filtersdata.items = this.selectedProduct.code
      }
      if (this.selectedBatches) {
        filtersdata.batches = this.selectedBatches.map(item => item.id)
      }
      if (this.startDateRange) {
        const [startDateFrom, startDateTo] = this.startDateRange.split(' to ')
        if (startDateFrom && startDateTo) {
          filtersdata.validity_start_date_from = startDateFrom
          filtersdata.validity_start_date_to = startDateTo
        }
      }
      if (this.endDateRange) {
        const [endDateFrom, endDateTo] = this.endDateRange.split(' to ')
        if (endDateFrom && endDateTo) {
          filtersdata.validity_end_date_from = endDateFrom
          filtersdata.validity_end_date_to = endDateTo
        }
      }

      this.$emit('filtersUpdated', filtersdata)
    },
  },
}
</script>
